// ─── Shared Types ────────────────────────────────────────────────────────────

export interface HealthRecord {
  id: string;
  recordNumber: string;
  date: string;
  flock: string;
  flockId: string;
  house: string;
  farm: string;
  symptoms: string[];
  suspectedCondition: string;
  diagnosis: string;
  veterinarian: string;
  treatment: string;
  medication: string;
  dosage: string;
  duration: string;
  birdsAffected: number;
  cost: number;
  status: 'open' | 'under-treatment' | 'resolved' | 'referred';
  notes: string;
  createdBy: string;
}

export interface VaccinationRecord {
  id: string;
  scheduleNumber: string;
  vaccine: string;
  disease: string;
  flock: string;
  flockId: string;
  house: string;
  farm: string;
  recommendedAgeDays: number;
  scheduledDate: string;
  actualDate: string | null;
  birdsScheduled: number;
  birdsVaccinated: number | null;
  batchNumber: string;
  expiryDate: string;
  administeredBy: string;
  veterinarian: string;
  route: string;
  dosePerBird: number;
  doseUnit: string;
  totalDosesUsed: number | null;
  inventoryItemId: string;
  inventoryItemName: string;
  cost: number;
  nextDoseDate: string | null;
  nextDoseVaccine: string | null;
  status: 'scheduled' | 'due' | 'completed' | 'missed' | 'overdue';
  notes: string;
}

export interface MedicationRecord {
  id: string;
  medicationNumber: string;
  medicine: string;
  flock: string;
  flockId: string;
  house: string;
  farm: string;
  reason: string;
  dosage: string;
  administrationMethod: string;
  startDate: string;
  endDate: string;
  durationDays: number;
  prescribedBy: string;
  administeredBy: string;
  withdrawalPeriodDays: number;
  withdrawalClearDate: string | null;
  inventoryItemId: string;
  inventoryItemName: string;
  quantityUsed: number;
  quantityUnit: string;
  costPerUnit: number;
  totalCost: number;
  status: 'active' | 'completed' | 'discontinued';
  withdrawalAlert: boolean;
  notes: string;
}

// ─── Mock Health Records ──────────────────────────────────────────────────────

export const MOCK_HEALTH_RECORDS: HealthRecord[] = [
  {
    id: 'hr-001',
    recordNumber: 'HR-2026-041',
    date: '03/09/2026',
    flock: 'BR-2026-002',
    flockId: 'flock-002',
    house: 'House A2',
    farm: 'Idudi Poultry Farm',
    symptoms: ['Sneezing', 'Nasal discharge', 'Reduced feed intake', 'Laboured breathing'],
    suspectedCondition: 'Infectious Bronchitis',
    diagnosis: 'Infectious Bronchitis (IB)',
    veterinarian: 'Dr. Nakato Sarah',
    treatment: 'Enrofloxacin 10% in drinking water + Vitamin C supplementation',
    medication: 'Enrofloxacin 10% Solution',
    dosage: '1ml per litre of water',
    duration: '5 days',
    birdsAffected: 320,
    cost: 174400,
    status: 'under-treatment',
    notes: 'Mortality slightly elevated. Monitor closely. Ensure adequate ventilation.',
    createdBy: 'Ogenga Paul',
  },
  {
    id: 'hr-002',
    recordNumber: 'HR-2026-040',
    date: '28/08/2026',
    flock: 'LY-2026-001',
    flockId: 'flock-004',
    house: 'Layer House 01',
    farm: 'Idudi Poultry Farm',
    symptoms: ['Watery droppings', 'Reduced egg production', 'Pale combs'],
    suspectedCondition: 'Coccidiosis',
    diagnosis: 'Coccidiosis (Eimeria spp.)',
    veterinarian: 'Dr. Nakato Sarah',
    treatment: 'Amprolium in drinking water for 5 days',
    medication: 'Amprolium 20% Solution',
    dosage: '2ml per litre of water',
    duration: '5 days',
    birdsAffected: 180,
    cost: 95000,
    status: 'resolved',
    notes: 'Egg production recovering. Full recovery expected within 2 weeks.',
    createdBy: 'Apio Christine',
  },
  {
    id: 'hr-003',
    recordNumber: 'HR-2026-039',
    date: '20/08/2026',
    flock: 'BR-2026-003',
    flockId: 'flock-003',
    house: 'House B1',
    farm: 'Idudi Poultry Farm',
    symptoms: ['Sudden death', 'Bloody droppings', 'Ruffled feathers'],
    suspectedCondition: 'Gumboro Disease (IBD)',
    diagnosis: 'Infectious Bursal Disease (IBD)',
    veterinarian: 'Dr. Mugisha Robert',
    treatment: 'Supportive therapy — electrolytes + vitamins. Strict biosecurity.',
    medication: 'Vitamin C + Electrolytes Powder',
    dosage: '1g per litre of water',
    duration: '7 days',
    birdsAffected: 68,
    cost: 42000,
    status: 'resolved',
    notes: 'Mortality peaked at day 3 then declined. Flock recovering well.',
    createdBy: 'Ogenga Paul',
  },
  {
    id: 'hr-004',
    recordNumber: 'HR-2026-038',
    date: '15/08/2026',
    flock: 'BRD-2026-001',
    flockId: 'flock-007',
    house: 'Breeder House 01',
    farm: 'Idudi Poultry Farm',
    symptoms: ['Swollen joints', 'Lameness', 'Reduced activity'],
    suspectedCondition: 'Marek\'s Disease',
    diagnosis: 'Marek\'s Disease (MD)',
    veterinarian: 'Dr. Nakato Sarah',
    treatment: 'Culling of severely affected birds. Supportive care for mild cases.',
    medication: 'Vitamin E + Selenium supplement',
    dosage: '0.5g per kg feed',
    duration: '14 days',
    birdsAffected: 12,
    cost: 28000,
    status: 'resolved',
    notes: '8 birds culled. Remaining birds recovering. Vaccination protocol reviewed.',
    createdBy: 'Apio Christine',
  },
  {
    id: 'hr-005',
    recordNumber: 'HR-2026-037',
    date: '10/08/2026',
    flock: 'LY-2026-002',
    flockId: 'flock-005',
    house: 'Layer House 02',
    farm: 'Idudi Poultry Farm',
    symptoms: ['Greenish diarrhoea', 'Lethargy', 'Reduced water intake'],
    suspectedCondition: 'Salmonellosis',
    diagnosis: 'Salmonella infection',
    veterinarian: 'Dr. Mugisha Robert',
    treatment: 'Oxytetracycline HCl 20% in drinking water',
    medication: 'Oxytetracycline HCl 20%',
    dosage: '1ml per litre of water',
    duration: '5 days',
    birdsAffected: 95,
    cost: 72000,
    status: 'resolved',
    notes: 'Egg withdrawal period observed. Eggs from this flock withheld for 7 days.',
    createdBy: 'Apio Christine',
  },
];

// ─── Mock Vaccination Records ─────────────────────────────────────────────────

export const MOCK_VACCINATIONS: VaccinationRecord[] = [
  {
    id: 'vac-001',
    scheduleNumber: 'VS-2026-081',
    vaccine: 'Newcastle Disease Vaccine (La Sota)',
    disease: 'Newcastle Disease (ND)',
    flock: 'BR-2026-003',
    flockId: 'flock-003',
    house: 'House B1',
    farm: 'Idudi Poultry Farm',
    recommendedAgeDays: 28,
    scheduledDate: '07/09/2026',
    actualDate: null,
    birdsScheduled: 3432,
    birdsVaccinated: null,
    batchNumber: 'VAC-ND-2026-041',
    expiryDate: '31/12/2026',
    administeredBy: 'Ogenga Paul',
    veterinarian: 'Dr. Nakato Sarah',
    route: 'Eye drop / Drinking water',
    dosePerBird: 1,
    doseUnit: 'dose',
    totalDosesUsed: null,
    inventoryItemId: 'inv-007',
    inventoryItemName: 'Newcastle Disease Vaccine (La Sota)',
    cost: 154440,
    nextDoseDate: '28/09/2026',
    nextDoseVaccine: 'Newcastle Disease Vaccine (La Sota) — Booster',
    status: 'due',
    notes: 'Day 28 ND booster. Ensure cold chain maintained during administration.',
  },
  {
    id: 'vac-002',
    scheduleNumber: 'VS-2026-080',
    vaccine: 'Gumboro (IBD) Vaccine',
    disease: 'Infectious Bursal Disease (IBD)',
    flock: 'BR-2026-003',
    flockId: 'flock-003',
    house: 'House B1',
    farm: 'Idudi Poultry Farm',
    recommendedAgeDays: 21,
    scheduledDate: '31/08/2026',
    actualDate: null,
    birdsScheduled: 3432,
    birdsVaccinated: null,
    batchNumber: 'VAC-IBD-2026-038',
    expiryDate: '28/02/2027',
    administeredBy: 'Ogenga Paul',
    veterinarian: 'Dr. Nakato Sarah',
    route: 'Drinking water',
    dosePerBird: 1,
    doseUnit: 'dose',
    totalDosesUsed: null,
    inventoryItemId: 'inv-008',
    inventoryItemName: 'Gumboro (IBD) Vaccine',
    cost: 178464,
    nextDoseDate: '14/09/2026',
    nextDoseVaccine: 'Gumboro (IBD) Vaccine — Booster',
    status: 'overdue',
    notes: 'OVERDUE — 7 days past schedule. Administer immediately.',
  },
  {
    id: 'vac-003',
    scheduleNumber: 'VS-2026-079',
    vaccine: 'Newcastle Disease Vaccine (La Sota)',
    disease: 'Newcastle Disease (ND)',
    flock: 'BR-2026-002',
    flockId: 'flock-002',
    house: 'House A2',
    farm: 'Idudi Poultry Farm',
    recommendedAgeDays: 42,
    scheduledDate: '02/09/2026',
    actualDate: '02/09/2026',
    birdsScheduled: 4614,
    birdsVaccinated: 4614,
    batchNumber: 'VAC-ND-2026-041',
    expiryDate: '31/12/2026',
    administeredBy: 'Ogenga Paul',
    veterinarian: 'Dr. Nakato Sarah',
    route: 'Eye drop',
    dosePerBird: 1,
    doseUnit: 'dose',
    totalDosesUsed: 4614,
    inventoryItemId: 'inv-007',
    inventoryItemName: 'Newcastle Disease Vaccine (La Sota)',
    cost: 207630,
    nextDoseDate: null,
    nextDoseVaccine: null,
    status: 'completed',
    notes: 'Final ND dose for this cycle. All birds vaccinated successfully.',
  },
  {
    id: 'vac-004',
    scheduleNumber: 'VS-2026-078',
    vaccine: 'Newcastle Disease Vaccine (La Sota)',
    disease: 'Newcastle Disease (ND)',
    flock: 'BR-2026-004',
    flockId: 'flock-008',
    house: 'House C1',
    farm: 'Kampala Demo Farm',
    recommendedAgeDays: 7,
    scheduledDate: '22/08/2026',
    actualDate: '22/08/2026',
    birdsScheduled: 5974,
    birdsVaccinated: 5974,
    batchNumber: 'VAC-ND-2026-041',
    expiryDate: '31/12/2026',
    administeredBy: 'Akello Grace',
    veterinarian: 'Dr. Mugisha Robert',
    route: 'Eye drop',
    dosePerBird: 1,
    doseUnit: 'dose',
    totalDosesUsed: 5974,
    inventoryItemId: 'inv-007',
    inventoryItemName: 'Newcastle Disease Vaccine (La Sota)',
    cost: 268830,
    nextDoseDate: '12/09/2026',
    nextDoseVaccine: 'Newcastle Disease Vaccine (La Sota) — Day 28',
    status: 'completed',
    notes: 'Day 7 priming dose. Next dose scheduled for day 28.',
  },
  {
    id: 'vac-005',
    scheduleNumber: 'VS-2026-077',
    vaccine: 'Marek\'s Disease Vaccine',
    disease: 'Marek\'s Disease (MD)',
    flock: 'LY-2026-003',
    flockId: 'flock-006',
    house: 'Layer House 03',
    farm: 'Kampala Demo Farm',
    recommendedAgeDays: 1,
    scheduledDate: '01/06/2026',
    actualDate: '01/06/2026',
    birdsScheduled: 4000,
    birdsVaccinated: 4000,
    batchNumber: 'VAC-MD-2026-019',
    expiryDate: '30/09/2026',
    administeredBy: 'Akello Grace',
    veterinarian: 'Dr. Mugisha Robert',
    route: 'Subcutaneous injection',
    dosePerBird: 1,
    doseUnit: 'dose',
    totalDosesUsed: 4000,
    inventoryItemId: 'inv-009',
    inventoryItemName: 'Marek\'s Disease Vaccine',
    cost: 272000,
    nextDoseDate: null,
    nextDoseVaccine: null,
    status: 'completed',
    notes: 'Day-old chick vaccination. All birds vaccinated at hatchery.',
  },
  {
    id: 'vac-006',
    scheduleNumber: 'VS-2026-082',
    vaccine: 'Newcastle Disease Vaccine (La Sota)',
    disease: 'Newcastle Disease (ND)',
    flock: 'BR-2026-004',
    flockId: 'flock-008',
    house: 'House C1',
    farm: 'Kampala Demo Farm',
    recommendedAgeDays: 28,
    scheduledDate: '12/09/2026',
    actualDate: null,
    birdsScheduled: 5974,
    birdsVaccinated: null,
    batchNumber: 'VAC-ND-2026-041',
    expiryDate: '31/12/2026',
    administeredBy: 'Akello Grace',
    veterinarian: 'Dr. Mugisha Robert',
    route: 'Drinking water',
    dosePerBird: 1,
    doseUnit: 'dose',
    totalDosesUsed: null,
    inventoryItemId: 'inv-007',
    inventoryItemName: 'Newcastle Disease Vaccine (La Sota)',
    cost: 268830,
    nextDoseDate: null,
    nextDoseVaccine: null,
    status: 'scheduled',
    notes: 'Day 28 booster dose. Prepare vaccine 30 minutes before administration.',
  },
  {
    id: 'vac-007',
    scheduleNumber: 'VS-2026-083',
    vaccine: 'Gumboro (IBD) Vaccine',
    disease: 'Infectious Bursal Disease (IBD)',
    flock: 'BR-2026-004',
    flockId: 'flock-008',
    house: 'House C1',
    farm: 'Kampala Demo Farm',
    recommendedAgeDays: 14,
    scheduledDate: '29/08/2026',
    actualDate: '29/08/2026',
    birdsScheduled: 5974,
    birdsVaccinated: 5974,
    batchNumber: 'VAC-IBD-2026-038',
    expiryDate: '28/02/2027',
    administeredBy: 'Akello Grace',
    veterinarian: 'Dr. Mugisha Robert',
    route: 'Drinking water',
    dosePerBird: 1,
    doseUnit: 'dose',
    totalDosesUsed: 5974,
    inventoryItemId: 'inv-008',
    inventoryItemName: 'Gumboro (IBD) Vaccine',
    cost: 310648,
    nextDoseDate: '12/09/2026',
    nextDoseVaccine: 'Gumboro (IBD) Vaccine — Booster',
    status: 'completed',
    notes: 'Day 14 IBD priming dose. Booster due at day 28.',
  },
];

// ─── Mock Medication Records ──────────────────────────────────────────────────

export const MOCK_MEDICATIONS: MedicationRecord[] = [
  {
    id: 'med-001',
    medicationNumber: 'MED-2026-031',
    medicine: 'Enrofloxacin 10% Solution',
    flock: 'BR-2026-002',
    flockId: 'flock-002',
    house: 'House A2',
    farm: 'Idudi Poultry Farm',
    reason: 'Infectious Bronchitis — secondary bacterial infection',
    dosage: '1ml per litre of drinking water',
    administrationMethod: 'Drinking water',
    startDate: '03/09/2026',
    endDate: '07/09/2026',
    durationDays: 5,
    prescribedBy: 'Dr. Nakato Sarah',
    administeredBy: 'Ogenga Paul',
    withdrawalPeriodDays: 7,
    withdrawalClearDate: '14/09/2026',
    inventoryItemId: 'inv-010',
    inventoryItemName: 'Enrofloxacin 10% Solution',
    quantityUsed: 2,
    quantityUnit: 'bottles (1L)',
    costPerUnit: 87200,
    totalCost: 174400,
    status: 'active',
    withdrawalAlert: true,
    notes: 'Birds must not be sold until 14/09/2026 (withdrawal period). Monitor daily.',
  },
  {
    id: 'med-002',
    medicationNumber: 'MED-2026-030',
    medicine: 'Vitamin C + Electrolytes Powder',
    flock: 'BR-2026-003',
    flockId: 'flock-003',
    house: 'House B1',
    farm: 'Idudi Poultry Farm',
    reason: 'Gumboro Disease — supportive therapy and stress recovery',
    dosage: '1g per litre of drinking water',
    administrationMethod: 'Drinking water',
    startDate: '20/08/2026',
    endDate: '26/08/2026',
    durationDays: 7,
    prescribedBy: 'Dr. Mugisha Robert',
    administeredBy: 'Ogenga Paul',
    withdrawalPeriodDays: 0,
    withdrawalClearDate: null,
    inventoryItemId: 'inv-011',
    inventoryItemName: 'Vitamin C + Electrolytes Powder',
    quantityUsed: 0.7,
    quantityUnit: 'kg',
    costPerUnit: 42000,
    totalCost: 29400,
    status: 'completed',
    withdrawalAlert: false,
    notes: 'No withdrawal period. Supportive supplement only.',
  },
  {
    id: 'med-003',
    medicationNumber: 'MED-2026-029',
    medicine: 'Oxytetracycline HCl 20%',
    flock: 'LY-2026-002',
    flockId: 'flock-005',
    house: 'Layer House 02',
    farm: 'Idudi Poultry Farm',
    reason: 'Salmonella infection — antibiotic treatment',
    dosage: '1ml per litre of drinking water',
    administrationMethod: 'Drinking water',
    startDate: '10/08/2026',
    endDate: '14/08/2026',
    durationDays: 5,
    prescribedBy: 'Dr. Mugisha Robert',
    administeredBy: 'Apio Christine',
    withdrawalPeriodDays: 7,
    withdrawalClearDate: '21/08/2026',
    inventoryItemId: 'inv-012',
    inventoryItemName: 'Oxytetracycline HCl 20%',
    quantityUsed: 1,
    quantityUnit: 'bottles (1L)',
    costPerUnit: 73500,
    totalCost: 73500,
    status: 'completed',
    withdrawalAlert: false,
    notes: 'Eggs withheld during treatment and 7 days after. Withdrawal period cleared 21/08/2026.',
  },
  {
    id: 'med-004',
    medicationNumber: 'MED-2026-028',
    medicine: 'Vitamin C + Electrolytes Powder',
    flock: 'BR-2026-002',
    flockId: 'flock-002',
    house: 'House A2',
    farm: 'Idudi Poultry Farm',
    reason: 'Heat stress recovery — supplementation',
    dosage: '1g per litre of drinking water',
    administrationMethod: 'Drinking water',
    startDate: '25/08/2026',
    endDate: '28/08/2026',
    durationDays: 4,
    prescribedBy: 'Dr. Nakato Sarah',
    administeredBy: 'Ogenga Paul',
    withdrawalPeriodDays: 0,
    withdrawalClearDate: null,
    inventoryItemId: 'inv-011',
    inventoryItemName: 'Vitamin C + Electrolytes Powder',
    quantityUsed: 0.4,
    quantityUnit: 'kg',
    costPerUnit: 42000,
    totalCost: 16800,
    status: 'completed',
    withdrawalAlert: false,
    notes: 'Preventive supplementation during hot spell.',
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

export const VACCINE_STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  scheduled: { label: 'Scheduled', color: '#3b82f6', bg: '#eff6ff' },
  due:        { label: 'Due',       color: '#f59e0b', bg: '#fffbeb' },
  completed:  { label: 'Completed', color: '#16a34a', bg: '#f0fdf4' },
  missed:     { label: 'Missed',    color: '#dc2626', bg: '#fef2f2' },
  overdue:    { label: 'Overdue',   color: '#dc2626', bg: '#fef2f2' },
};

export const HEALTH_STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  'open':             { label: 'Open',             color: '#dc2626', bg: '#fef2f2' },
  'under-treatment':  { label: 'Under Treatment',  color: '#f59e0b', bg: '#fffbeb' },
  'resolved':         { label: 'Resolved',         color: '#16a34a', bg: '#f0fdf4' },
  'referred':         { label: 'Referred',         color: '#7c3aed', bg: '#f5f3ff' },
};

export const MED_STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  'active':        { label: 'Active',        color: '#f59e0b', bg: '#fffbeb' },
  'completed':     { label: 'Completed',     color: '#16a34a', bg: '#f0fdf4' },
  'discontinued':  { label: 'Discontinued',  color: '#6b7280', bg: '#f9fafb' },
};

export const FLOCKS_LIST = [
  { id: 'flock-002', number: 'BR-2026-002', name: 'Batch A2 — Ross 308', house: 'House A2', farm: 'Idudi Poultry Farm', currentQty: 4614 },
  { id: 'flock-003', number: 'BR-2026-003', name: 'Batch B1 — Cobb 500', house: 'House B1', farm: 'Idudi Poultry Farm', currentQty: 3432 },
  { id: 'flock-004', number: 'LY-2026-001', name: 'Layers Batch L1 — Lohmann', house: 'Layer House 01', farm: 'Idudi Poultry Farm', currentQty: 7742 },
  { id: 'flock-005', number: 'LY-2026-002', name: 'Layers Batch L2 — Isa Brown', house: 'Layer House 02', farm: 'Idudi Poultry Farm', currentQty: 6318 },
  { id: 'flock-006', number: 'LY-2026-003', name: 'Layers Batch L3 — Lohmann', house: 'Layer House 03', farm: 'Kampala Demo Farm', currentQty: 3940 },
  { id: 'flock-007', number: 'BRD-2026-001', name: 'Breeders Flock BD1 — Ross', house: 'Breeder House 01', farm: 'Idudi Poultry Farm', currentQty: 2358 },
  { id: 'flock-008', number: 'BR-2026-004', name: 'Batch C1 — Arbor Acres', house: 'House C1', farm: 'Kampala Demo Farm', currentQty: 5974 },
];

export const VACCINE_INVENTORY = [
  { id: 'inv-007', name: 'Newcastle Disease Vaccine (La Sota)', currentQty: 12, unit: 'doses×1000' },
  { id: 'inv-008', name: 'Gumboro (IBD) Vaccine', currentQty: 28, unit: 'doses×1000' },
  { id: 'inv-009', name: "Marek\'s Disease Vaccine", currentQty: 8, unit: 'doses×1000' },
];

export const MEDICATION_INVENTORY = [
  { id: 'inv-010', name: 'Enrofloxacin 10% Solution', currentQty: 3, unit: 'bottles (1L)' },
  { id: 'inv-011', name: 'Vitamin C + Electrolytes Powder', currentQty: 8.5, unit: 'kg' },
  { id: 'inv-012', name: 'Oxytetracycline HCl 20%', currentQty: 6, unit: 'bottles (1L)' },
];
